import {
  applyBoardConditionStep,
  CONDITION_DIE_FLOOR,
  getConditionFriction,
  getConditionGravity,
  normalizeBoardCondition,
  startConditionThrow,
} from './boardConditions';

export const MIN_SLINGSHOT_POWER = 0.18;
export const MAX_SLINGSHOT_ANGLE = Math.PI * (50 / 180);
export const SLINGSHOT_ORIGIN = { x: 0, y: -0.22, z: 9.8 };
// Short views need less finger travel so camera framing can favor the tiles.
// Power stays normalized: a full pull produces the same throw on every screen.
export const getMaxSlingshotPull = (width, height = Infinity) =>
  Math.min(140, Math.max(72, width * 0.12), Math.max(48, height * 0.19));

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

export const normalizeSlingshotLaunch = ({
  power = 0.65,
  angle = 0,
} = {}) => ({
  power: clamp(Number.isFinite(power) ? power : 0.65, 0, 1),
  angle: clamp(
    Number.isFinite(angle) ? angle : 0,
    -MAX_SLINGSHOT_ANGLE,
    MAX_SLINGSHOT_ANGLE
  ),
});

export const getSlingshotPull = (dx, dy, maxPull) => {
  const down = Math.max(0, dy);
  const side = clamp(
    dx,
    -down * Math.tan(MAX_SLINGSHOT_ANGLE),
    down * Math.tan(MAX_SLINGSHOT_ANGLE)
  );
  return normalizeSlingshotLaunch({
    power: Math.min(1, Math.hypot(side, down) / maxPull),
    angle: down > 0 ? -Math.atan2(side, down) : 0,
  });
};

export const getSlingshotOrigin = (launch) => {
  const { power, angle } = normalizeSlingshotLaunch(launch);
  return {
    x: -Math.sin(angle) * power * 0.85,
    y: SLINGSHOT_ORIGIN.y,
    z: SLINGSHOT_ORIGIN.z + Math.cos(angle) * power * 1.05,
  };
};

// Inbound throws can cross the near edge before the board's walls contain them.
export const applySlingshotBounds = (physics, position, edge = 6.62) => {
  if (position.z <= edge) physics.enteredBoard = true;
  if (Math.abs(position.x) > edge) {
    position.x = Math.sign(position.x) * edge;
    physics.velocity.x *= -0.78;
  }
  if (position.z < -edge || (physics.enteredBoard && position.z > edge)) {
    position.z = Math.sign(position.z) * edge;
    physics.velocity.z *= -0.78;
  }
};

// The preview and the actual throw use the same pulled position and velocity.
// It predicts the first bounce; cube rotation, later bounces, and bumps remain physical.
export const getSlingshotVelocity = (launch) => {
  const { power, angle } = normalizeSlingshotLaunch(launch);
  const speed = 4.8 + power * 14;
  return {
    x: Math.sin(angle) * speed,
    y: 2.7 + power * 0.8,
    z: -Math.cos(angle) * speed,
  };
};

const simulateConditionThrow = (launch, condition, firstBounce = false) => {
  const position = getSlingshotOrigin(launch);
  const physics = {
    velocity: getSlingshotVelocity(launch),
    active: true,
    onGround: false,
    enteredBoard: false,
  };
  startConditionThrow(physics, condition);
  const points = [{ ...position }];
  const step = 1 / 120;
  let resting = 0;
  for (let elapsed = 0; elapsed < 7.5; elapsed += step) {
    applyBoardConditionStep(physics, position, step);
    physics.velocity.y -= getConditionGravity(physics.boardCondition) * step;
    position.x += physics.velocity.x * step;
    position.y += physics.velocity.y * step;
    position.z += physics.velocity.z * step;
    physics.onGround = position.y <= CONDITION_DIE_FLOOR;
    applySlingshotBounds(physics, position);
    if (physics.onGround) {
      position.y = CONDITION_DIE_FLOOR;
      if (firstBounce) {
        points.push({ ...position });
        break;
      }
      physics.velocity.y =
        Math.abs(physics.velocity.y) > 0.85 ? -physics.velocity.y * 0.3 : 0;
      const speed = Math.hypot(physics.velocity.x, physics.velocity.z);
      const drag = Math.max(
        0,
        speed -
          9.8 *
            0.58 *
            getConditionFriction(physics.boardCondition, position) *
            step
      );
      if (speed > 0) {
        physics.velocity.x *= drag / speed;
        physics.velocity.z *= drag / speed;
      }
      resting = drag < 0.2 && physics.velocity.y < 0.12 ? resting + step : 0;
      if (resting >= 0.1) break;
    } else resting = 0;
    points.push({ ...position });
  }
  return { points, target: { ...position } };
};

export const getSlingshotPreview = (launch, boardCondition = null) => {
  const condition = normalizeBoardCondition(boardCondition);
  if (condition) {
    const simulated = simulateConditionThrow(launch, condition, true);
    return {
      points: Array.from(
        { length: 41 },
        (_, index) =>
          simulated.points[
            Math.round((index / 40) * (simulated.points.length - 1))
          ]
      ),
      target: simulated.target,
    };
  }
  const velocity = getSlingshotVelocity(launch);
  const origin = getSlingshotOrigin(launch);
  const gravity = 9.8;
  const floor = -0.815;
  const duration =
    (velocity.y +
      Math.sqrt(velocity.y ** 2 + 2 * gravity * (origin.y - floor))) /
    gravity;
  const edge = 6.62;
  const reflect = (value, inbound = false) => {
    if (value > edge && !inbound) return edge - (value - edge) * 0.78;
    if (value < -edge) return -edge + (-edge - value) * 0.78;
    return value;
  };
  const points = Array.from({ length: 41 }, (_, index) => {
    const time = (index / 40) * duration;
    return {
      x: reflect(origin.x + velocity.x * time),
      y: origin.y + velocity.y * time - 0.5 * gravity * time * time,
      z: reflect(origin.z + velocity.z * time, true),
    };
  });
  return { points, target: points[points.length - 1] };
};

const targetTile = (target) => {
  const column = clamp(Math.round(target.x / 2.85) + 2, 0, 4);
  const row = clamp(Math.round(target.z / 2.85) + 2, 0, 4);
  return row * 5 + column;
};

export const getSlingshotPreviewTile = (launch, boardCondition = null) =>
  targetTile(getSlingshotPreview(launch, boardCondition).target);

export const getSlingshotFallbackTile = (launch, boardCondition = null) => {
  const condition = normalizeBoardCondition(boardCondition);
  return condition
    ? targetTile(simulateConditionThrow(launch, condition).target)
    : getSlingshotPreviewTile(launch);
};
